import { historyJSON } from './editorHistory.js';
import { validateWallEditSite, WALL_THICKNESS_LIMITS } from './wallThickness.js';
import { buildingID, siteLevels } from './siteBuildings.js';
import { areaEnvironment } from './siteAreas.js';
import { buildingVisibility } from './buildingVisibility.js';
import { findCeilingMount, syncCeilingCameras } from './ceilingMounting.js';
import { syncPoleCameras } from './poleMounting.js';
import { isCeilingCamera } from './cameraMounts.js';
import { stairSpec, stairFootprint } from './stairs.js';
import { ptInPoly, distToSeg, segHit } from './geometry.js';

const reviews = new WeakMap();
const fail = message => { throw new Error(message); };
const object = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const finite = v => typeof v === 'number' && Number.isFinite(v);
export const isIndoorRoomArea = area => area?.kind === 'floor' && areaEnvironment(area) === 'indoor';
const same = (a, b) => historyJSON(a) === historyJSON(b);
const cameraRecords = doc => siteLevels(doc).map(f => [f.id, f.plan.cams]);
function polygonsMeet(a, b) {
  const contains = (p, poly) => ptInPoly(p.x, p.y, poly) || poly.some((q, i) => {
    const r = poly[(i + 1) % poly.length]; return distToSeg(p.x, p.y, q.x, q.y, r.x, r.y) <= 1e-6;
  });
  return a.some(p => contains(p, b)) || b.some(p => contains(p, a)) || a.some((p, i) => b.some((q, j) => {
    const r = a[(i + 1) % a.length], s = b[(j + 1) % b.length];
    return segHit(p.x, p.y, r.x, r.y, q.x, q.y, s.x, s.y) !== null;
  }));
}
function checkedLevels(plan) {
  try { return validateWallEditSite(plan); }
  catch (error) { throw new Error(error.message.replace(/editing this wall|editing thickness|wall-edit/g, 'room removal')); }
}
function preserveDependencies(plan, doc, levels, area) {
  const active = levels.find(f => f.id === plan.activeFloorId);
  // Do not normalize malformed heights/environment into a different physical scene.
  for (const f of levels) for (const a of f.plan.areas) {
    if (a.environment !== undefined && !['indoor', 'outdoor'].includes(a.environment) ||
        a.roofHeight !== undefined && (!finite(a.roofHeight) || a.roofHeight < 0 || a.roofHeight > 100000))
      fail('An existing room has an invalid environment or ceiling height. Review it before removing this room.');
  }
  const scenarios = plan.lighting?.scenarios;
  if (!Array.isArray(scenarios) || scenarios.length > 12) fail('The saved lighting scenarios need review before removing this room.');
  for (const scenario of scenarios) {
    if (!object(scenario)) fail('A saved lighting scenario is malformed.');
    if (scenario.layoutReview != null) {
      const r = scenario.layoutReview;
      if (!object(r) || typeof r.floorId !== 'string' || r.areaId != null && typeof r.areaId !== 'string') fail('A saved lighting layout has an unreadable room target.');
      if (r.floorId === active.id && r.areaId === area.id) fail('A saved lighting layout review targets this room. Remove or retarget that lighting review before removing the room.');
    }
  }
  const roof = area.roofHeight || 0;
  for (const stair of plan.stairs) {
    const spec = stairSpec(plan, stair);
    if (spec.lower.elevation <= active.elevation + roof + .2 && spec.upper.elevation >= active.elevation - .16 && polygonsMeet(area.pts, stairFootprint(stair)))
      fail('A stair or landing crosses this room floor or ceiling. Review its floor connections before removing the room.');
  }
  const ceilings = levels.flatMap(f => f.plan.cams.filter(c => isCeilingCamera(f.plan, c)).map(c => ({ f, c })));
  if (ceilings.length) {
    const before = buildingVisibility(plan), after = buildingVisibility(doc);
    if (Math.max(before.walls.length, after.walls.length) * ceilings.length > WALL_THICKNESS_LIMITS.mountComparisons)
      fail('This Site exceeds the bounded room-removal camera-support review.');
    for (const { f, c } of ceilings) {
      const p = { ...f.plan, activeFloorId: f.id, elevation: f.elevation };
      const old = findCeilingMount(p, c, before), next = findCeilingMount(p, c, after);
      if (!!old !== !!next || old && (old.key !== next.key || Math.abs(old.height - next.height) > 1e-9))
        fail('Removing this room would change a ceiling camera support, including on another floor. Move or review the camera first.');
      const close = ptInPoly(c.x, c.y, area.pts) || area.pts.some((a, i) => {
        const b = area.pts[(i + 1) % area.pts.length]; return distToSeg(c.x, c.y, a.x, a.y, b.x, b.y) <= 1;
      });
      let recorded;
      try { recorded = JSON.parse(c.mountSurface || 'null'); } catch { recorded = null; }
      const namesRemoved = Array.isArray(recorded) && recorded[0] === active.id && recorded[2] === area.id;
      if (!old && (namesRemoved || close && c.h + f.elevation <= active.elevation + roof + .2 && c.h + f.elevation >= active.elevation - 1))
        fail('A ceiling camera near this room has unresolved support. Review its mount before removing the room.');
    }
  }
  // Ordinary Store.touch runs both synchronizers. Neither may change even an
  // unrelated retained camera as a side effect of this reviewed edit.
  const synced = structuredClone(doc); syncCeilingCameras(synced); syncPoleCameras(synced);
  if (!same(cameraRecords(synced), cameraRecords(doc))) fail('Camera synchronization would change a saved camera. Review its support before removing the room.');
}

/** Remove one canonical indoor area only. Its original evidence is retained by
 * the ordinary Undo snapshot, not transferred or fabricated on surviving walls. */
export function reviewRoomAreaRemoval(plan, selection) {
  const fingerprint = historyJSON(plan), levels = checkedLevels(plan);
  if (!object(selection) || typeof selection.id !== 'string' || selection.type !== undefined && selection.type !== 'area' ||
      selection.floorId !== undefined && selection.floorId !== plan.activeFloorId || selection.buildingId !== undefined && selection.buildingId !== buildingID(plan))
    fail('Choose the room on its owning building and floor before reviewing removal.');
  const matches = plan.areas.filter(a => a.id === selection.id);
  if (matches.length !== 1 || !isIndoorRoomArea(matches[0])) fail('Choose one existing indoor room floor to remove.');
  const area = matches[0];
  if (area.locked) fail('Unlock this room before removing its floor and roof.');
  const doc = structuredClone(plan); doc.areas = doc.areas.filter(a => a.id !== area.id);
  preserveDependencies(plan, doc, levels, area);
  const qualified = { type: 'area', id: area.id, floorId: plan.activeFloorId, buildingId: buildingID(plan) };
  const result = { doc, area: structuredClone(area), selection: qualified, warnings: [
    'Only this room floor and roof are removed. Walls, openings, cameras and contents stay in place.',
    'The room name, requirements and its own original evidence leave the current plan. Undo restores the full room while that history is retained.',
    'Private photos are not deleted. Pending photos for this room may need an explicit new destination.',
  ] };
  reviews.set(result, { fingerprint, candidate: historyJSON(doc), selection: historyJSON(qualified), area: historyJSON(result.area) });
  return result;
}

export function applyRoomAreaRemovalReview(store, reviewed) {
  const proof = reviews.get(reviewed);
  if (!proof || !store || store._edit || historyJSON(store.doc) !== proof.fingerprint ||
      historyJSON(reviewed.doc) !== proof.candidate || historyJSON(reviewed.selection) !== proof.selection || historyJSON(reviewed.area) !== proof.area)
    fail('The Site or reviewed room changed. Finish editing and review removal again.');
  const doc = structuredClone(reviewed.doc);
  store.snapshot(); store.doc = doc; store.sel = null; reviews.delete(reviewed); store.touch(); return doc;
}
